/**
 * UploadModal — DE-171 media upload (super-admin, on the Content page).
 *
 * Uploads land in the Storage staging pool; the operator Moves them into a device
 * collection afterwards (no collection picker here). Phase-5 flow with the strict
 * "confirm-before-transcode" gate:
 *   1. On file-select, probe the file IN-BROWSER (resolution/duration + codec) and show
 *      whether the chosen format needs a re-encode — so a big 4K master can be cancelled
 *      BEFORE upload.
 *   2. presign (POST /content/uploads → uploadId) + raw XHR PUT to S3.
 *   3. The content-processor PROBE phase decides: passthrough (stored as-is, no re-encode)
 *      → done; re-encode needed → 'pending-confirmation' (the gate); too long / bad → failed.
 *   4. Poll GET /content/uploads/:id. On the gate, show the exact decision and require
 *      Confirm (→ async transcode) or Cancel — a re-encode NEVER starts silently.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/Toast';
import { useUploadPresign, type UploadStatus } from '../api/hooks';
import { api } from '../api/client';
import { putToS3, pollUpload } from '../components/s3upload';
import { probeMedia, type MediaProbe } from '../components/probeMedia';

type Phase = 'idle' | 'uploading' | 'probing' | 'confirm' | 'transcoding';
type Format = 'h264' | 'hevc';


function fmtDur(s: number | null): string {
  if (s == null) return '—';
  const m = Math.floor(s / 60), sec = Math.round(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Pre-upload re-encode estimate from the in-browser probe (server is authoritative).
function estimate(probe: MediaProbe, format: Format):
  { needs: boolean | null; text: string } {
  if (probe.isAudio) return { needs: null, text: 'Audio — re-encoded to AAC unless it already is.' };
  if (probe.codec === null)
    return { needs: null, text: "Codec couldn't be read here — the server will decide." };
  const t = format === 'hevc'
    ? { codec: 'hevc', label: 'H.265 (4K)', w: 3840, h: 2160 }
    : { codec: 'h264', label: 'H.264 (1080p)', w: 1920, h: 1080 };
  const over = (!!probe.width && probe.width > t.w) || (!!probe.height && probe.height > t.h);
  const wrong = probe.codec !== t.codec;
  if (wrong || over) {
    const why = [
      wrong ? `${probe.codec.toUpperCase()} → ${t.codec.toUpperCase()}` : '',
      over ? `${probe.width}×${probe.height} over the cap` : '',
    ].filter(Boolean).join(', ');
    return { needs: true, text: `Will be re-encoded to ${t.label} (${why}).` };
  }
  return { needs: false, text: `Already ${t.label} — uploads as-is, no re-encode.` };
}


export function UploadModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const presign = useUploadPresign();
  const [file, setFile] = useState<File | null>(null);
  const [probe, setProbe] = useState<MediaProbe | null>(null);
  const [format, setFormat] = useState<Format>('h264');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [gate, setGate] = useState<UploadStatus | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  // DE-234 — a .vtt is not a media file: it belongs TO an item, so it needs the
  // item and its language rather than a target codec. Both are required, and the
  // server derives the stored filename from them.

  const busy = phase !== 'idle' && phase !== 'confirm';

  // A sidecar has nothing to probe — no duration, no codec, no re-encode
  // decision — so the whole media flow is skipped for it.
  const isSidecar = !!file && /\.(vtt|json)$/i.test(file.name);
  const isSubtitle = !!file && /\.vtt$/i.test(file.name);

  const pickFile = async (f: File | null) => {
    setFile(f); setProbe(null);
    if (f && !/\.(vtt|json)$/i.test(f.name)) setProbe(await probeMedia(f).catch(() => null));
  };

  const finishOk = (msg: string) => {
    qc.invalidateQueries({ queryKey: ['content'] });
    toast.success(msg);
    onClose();
  };

  // Branch on the polled status: gate → confirm UI; done → success; failed → error.
  const handleResult = (s: UploadStatus | null) => {
    if (!s) { finishOk('Uploaded — still processing; it will appear in Storage shortly.'); return; }
    if (s.status === 'pending-confirmation') { setGate(s); setPhase('confirm'); return; }
    if (s.status === 'done') {
      finishOk(s.decision === 'passthrough'
        ? `${s.relpath} stored as-is (no re-encode) — Move it to a collection.`
        : `${s.relpath} re-encoded and ready in Storage — Move it to a collection.`);
      return;
    }
    if (s.status === 'failed') { toast.error(`Upload rejected: ${s.error ?? 'unknown error'}`); setPhase('idle'); return; }
    finishOk('Upload finished.');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { toast.error('Pick a file to upload.'); return; }
    try {
      if (isSubtitle) {
        toast.error('Attach subtitles from the video\u2019s Edit panel in the Library.');
        setPhase('idle');
        return;
      }
      setPhase('uploading'); setProgress(0);
      const fmt = isSidecar ? 'sidecar' : probe?.isAudio ? 'audio' : format;
      const { uploadUrl, uploadId: id } = await presign.mutateAsync({
        filename: file.name, format: fmt,

      });
      setUploadId(id);
      await putToS3(uploadUrl, file, setProgress);
      setPhase('probing');
      handleResult(await pollUpload(id));
    } catch (err) {
      toast.error(`Upload failed: ${(err as Error).message}`);
      setPhase('idle');
    }
  };

  const confirmTranscode = async () => {
    if (!uploadId) return;
    try {
      setPhase('transcoding'); setGate(null);
      await api(`/content/uploads/${encodeURIComponent(uploadId)}/confirm`, { method: 'POST' });
      handleResult(await pollUpload(uploadId));
    } catch (err) {
      toast.error(`Transcode failed to start: ${(err as Error).message}`);
      setPhase('idle');
    }
  };

  const cancelGate = async () => {
    if (!uploadId) { onClose(); return; }
    try { await api(`/content/uploads/${encodeURIComponent(uploadId)}/cancel`, { method: 'POST' }); }
    catch { /* best-effort */ }
    toast.success('Upload cancelled — nothing was re-encoded.');
    onClose();
  };

  const est = probe ? estimate(probe, format) : null;
  const detected = probe && !probe.isAudio
    ? `${(probe.codec ?? 'unknown').toUpperCase()} · ${probe.width ?? '?'}×${probe.height ?? '?'} · ${fmtDur(probe.durationS)}`
    : probe ? `audio · ${fmtDur(probe.durationS)}` : null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-modal">
      <form onSubmit={submit} className="card w-full max-w-md space-y-4">
        <div>
          <h2 className="font-semibold">Upload media</h2>
          {/* DE-247 — the sequence, stated on OPEN rather than revealed field by
              field. Publishing one item is several non-obvious steps and nothing
              here said so, which is how a subtitle ends up uploaded on its own. */}
          <ol className="text-xs text-muted mt-1 space-y-0.5 list-decimal list-inside">
            <li>Upload the video or audio here — it lands in <span className="font-medium">Storage</span>.</li>
            <li>In the Library, press <span className="font-medium">Edit</span> to set its title,
                description and spoken language, and to attach subtitles.</li>
            <li><span className="font-medium">Move</span> it into a collection.</li>
            <li>Add it to a profile, then deploy from the <span className="font-medium">Releases</span> page.</li>
          </ol>
          <p className="text-xs text-muted mt-2">
            A re-encode is always confirmed first — a file already in the chosen format is
            stored as-is.
          </p>
        </div>

        {/* Confirm-before-transcode gate (server decided a re-encode is needed). */}
        {phase === 'confirm' && gate ? (
          <div className="space-y-3">
            <div className="pill-warn inline-flex">re-encode required</div>
            <p className="text-sm">
              <span className="font-medium">{gate.relpath}</span> isn’t in the target format and
              needs re-encoding before it can be stored.
            </p>
            <p className="text-xs text-muted">
              {gate.src_codec ? `${gate.src_codec.toUpperCase()} ` : ''}
              {gate.src_width ? `${gate.src_width}×${gate.src_height} ` : ''}
              {gate.duration_s ? `· ${fmtDur(gate.duration_s)} ` : ''}
              → {gate.format.toUpperCase()}{gate.reason ? ` — ${gate.reason}` : ''}
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={cancelGate}
                className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft">
                Cancel
              </button>
              <button type="button" onClick={confirmTranscode}
                className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90">
                Re-encode &amp; upload
              </button>
            </div>
          </div>
        ) : (
          <>
            <input
              type="file"
              accept="video/*,audio/*,.vtt,.json"
              disabled={busy}
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-md
                         file:border-0 file:bg-soft file:text-fg file:font-medium"
            />
            {file && (
              <p className="text-xs text-muted">
                {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                {detected ? ` · detected ${detected}` : ' · reading…'}
              </p>
            )}

            {/* A subtitle is a PROPERTY of a video, not an asset of its own. Asking
                for it here forced the operator to name the item, pick it from a
                list, and have their filename discarded — friction that existed
                only because the UI modelled it wrong. Attach it from the item's
                Edit panel instead, where the item is already known. (DE-247) */}
            {isSubtitle && (
              <div className="space-y-1 rounded-md border border-[var(--warn)] p-3">
                <p className="text-xs">
                  Subtitles are attached to their video, not uploaded on their own.
                </p>
                <p className="text-xs text-muted">
                  Open the video in the Library, press <span className="font-medium">Edit</span>,
                  and add the track there — the filename and collection are worked out for you.
                </p>
              </div>
            )}

            {isSidecar && !isSubtitle && (
              <p className="text-xs text-muted">
                Metadata sidecar — stored as-is and matched to its item by filename.
              </p>
            )}

            {probe && !probe.isAudio && !isSidecar && (
              <label className="block text-sm space-y-1">
                <span className="text-muted">Target format</span>
                <select
                  value={format}
                  disabled={busy}
                  onChange={(e) => setFormat(e.target.value as Format)}
                  className="block w-full border border-border rounded-md px-2 py-1.5 text-sm disabled:opacity-50"
                >
                  <option value="h264">H.264 (1080p — plays on all devices)</option>
                  <option value="hevc">H.265 (4K — FFmpeg-backend devices)</option>
                </select>
              </label>
            )}

            {est && (
              <p className={`text-xs ${est.needs === true ? 'text-warn' : est.needs === false ? 'text-accent' : 'text-muted'}`}>
                {est.text}
                {est.needs === true && ' You’ll confirm the re-encode after upload.'}
              </p>
            )}

            {phase === 'uploading' && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted">
                  <span>Uploading…</span><span>{progress}%</span>
                </div>
                <div className="w-full bg-soft rounded-full h-2">
                  <div className="bg-accent h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            )}
            {phase === 'probing' && <p className="text-sm text-muted">Checking the file on the server…</p>}
            {phase === 'transcoding' && <p className="text-sm text-muted">Re-encoding on the server — this can take a moment…</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} disabled={busy}
                className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft disabled:opacity-50">
                Cancel
              </button>
              <button type="submit" disabled={!file || busy}
                className="px-3 py-1.5 bg-accent text-white rounded-md text-sm font-medium hover:bg-accent/90 disabled:opacity-50">
                {phase === 'uploading' ? 'Uploading…' : phase === 'probing' || phase === 'transcoding' ? 'Processing…' : 'Upload'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
